/**
 * ====================================================================
 * FIKO CAD DIMENSION INTEGRATION SERVICE
 * ====================================================================
 *
 * Cetvel ve Kumpas bileşenleri ile yeni Dimension sistemi arasında
 * köprü görevi gören servis.
 *
 * Mevcut FikoOlcumCizgisi sistemini yeni profesyonel dimension
 * sistemine bağlar.
 *
 * v2.0.0 Yenilikler:
 * - Sürükle-bırak desteği (FikoDimensionInteraction entegrasyonu)
 * - Otomatik canvas bağlama
 * - Hover ve seçim efektleri
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { FikoSahne } from '../../FikoSahne';
import { FikoOlcumCizgisi, OlcumTuru } from '../../FikoOlcumCizgisi';
import { FikoOlcumler } from '../../FikoOlcumler';
import {
    DimensionManager,
    DimensionType,
    ReferenceType,
    GeometryReference,
    LinearDimensionOptions,
    AngularDimensionOptions,
    DiameterDimensionOptions,
    AxisLock,
    DimensionSettings
} from './index';
import { FikoDimensionInteraction, createDimensionInteraction } from './advanced/FikoDimensionInteraction';

/**
 * Ölçüm sonucu interface'i (Cetvel/Kumpas ile uyumlu)
 */
export interface OlcumSonucu {
    basarili: boolean;
    mesafe?: number;
    aci?: number;
    cap?: number;
    yaricap?: number;
    birim: string;
    tip: OlcumTuru;
    noktalar?: {
        baslangic: THREE.Vector3;
        bitis: THREE.Vector3;
    };
    merkezBilgisi?: {
        merkez: THREE.Vector3;
        yaricap: number;
        tip?: string;
    };
}

/**
 * Dimension Integration Service
 */
export class DimensionIntegrationService {
    // Manager instance
    private manager: DimensionManager | null = null;

    // Sahne referansı
    private scene: FikoSahne | null = null;

    // Mevcut FikoOlcumler grubu
    private fikoOlcumlerGrubu: FikoOlcumler | null = null;

    // 🎯 v2.0.0: Sürükle-bırak etkileşim yöneticisi
    private interactionManager: FikoDimensionInteraction | null = null;

    // Canvas referansı
    private canvas: HTMLElement | null = null;

    // Ayarlar
    private ayarlar = {
        birim: 'mm',
        hassasiyet: 2,
        siyahStil: true,  // CATIA tarzı siyah çizgiler
        surukleAktif: true  // 🎯 Sürükleme aktif mi
    };

    /**
     * Servisi başlat
     */
    initialize(scene: FikoSahne, fikoOlcumlerGrubu?: FikoOlcumler): void {
        this.scene = scene;
        this.fikoOlcumlerGrubu = fikoOlcumlerGrubu || null;

        // Dimension Manager oluştur
        this.manager = new DimensionManager(scene);

        // 🎯 v2.0.0: Canvas'ı otomatik bul ve sürükleme desteğini başlat
        this.initializeDragSupport();
    }

    /**
     * 🎯 v2.0.0: Sürükleme desteğini başlat
     */
    private initializeDragSupport(): void {
        // Canvas'a erişim için küçük bir gecikme (DOM hazır olsun)
        setTimeout(() => {
            const canvas = this.scene?.anaGosterim?.sunucu?.domElement as HTMLElement;
            if (canvas && this.manager) {
                this.canvas = canvas;

                // DimensionManager'ın kendi DraggableSupport'unu aktifleştir
                this.manager.enableDragSupport(canvas);

                // console.log('[DimensionIntegration] Drag support enabled');
            }
        }, 100);
    }

    /**
     * 🎯 v2.0.0: Sürükleme desteğini manuel başlat (kumpas/cetvel tarafından çağrılabilir)
     */
    enableDragSupport(canvas?: HTMLElement): void {
        const targetCanvas = canvas || this.canvas || this.scene?.anaGosterim?.sunucu?.domElement as HTMLElement;

        if (targetCanvas && this.manager) {
            this.canvas = targetCanvas;
            this.manager.enableDragSupport(targetCanvas);
            this.ayarlar.surukleAktif = true;
            // console.log('[DimensionIntegration] Drag support manually enabled');
        }
    }

    /**
     * 🎯 v2.0.0: Sürükleme desteğini devre dışı bırak
     */
    disableDragSupport(): void {
        if (this.manager) {
            this.manager.disableDragSupport();
            this.ayarlar.surukleAktif = false;
        }
    }

    /**
     * 🎯 v2.0.0: Sürükleme aktif mi kontrol et
     * Bu metod kumpas/cetvel gibi bileşenlerin tıklama olaylarında
     * sürükleme sırasında temizleme yapmamasını sağlar
     *
     * @returns true eğer bir dimension sürükleniyorsa
     */
    isDragging(): boolean {
        return this.manager?.isDragging() ?? false;
    }

    /**
     * 🎯 v2.0.0: Bir dimension üzerinde hover var mı kontrol et
     *
     * @returns true eğer bir dimension üzerinde hover varsa
     */
    isHovering(): boolean {
        return this.manager?.isHovering() ?? false;
    }

    /**
     * 🎯 v2.0.0: Sürükleme veya hover aktif mi kontrol et
     * Herhangi bir etkileşim varsa true döner
     */
    isInteracting(): boolean {
        return this.isDragging() || this.isHovering();
    }

    /**
     * v2.0.0 - DimensionSettings'den mevcut stil seçeneklerini al
     * 🎯 v3.2.6: backgroundColor ve backgroundOpacity eklendi (Sprite arka plan desteği)
     */
    private getCurrentStyleOptions(): Partial<LinearDimensionOptions> {
        const settings = DimensionSettings.getInstance().getSettings();
        return {
            color: settings.colors.dimensionLine ?? settings.colors.normal,
            lineWidth: settings.dimensionLine?.width ?? 1.5,
            arrowSize: settings.arrow?.size ?? 3,
            arrowStyle: settings.arrow?.type,
            fontSize: settings.label?.fontSize ?? 12,
            textColor: settings.label?.textColor ?? '#ffffff', // 🎯 BEYAZ yazı
            backgroundColor: settings.label?.backgroundColor ?? '#000000', // 🎯 SİYAH arkaplan
            backgroundOpacity: settings.label?.backgroundOpacity ?? 1, // 🎯 Tam opak
            showExtensionLines: true,
            extensionLineGap: 0.5,        // 🎯 Referans noktasına daha yakın başla
            extensionLineOvershoot: 3,    // 🎯 Dimension line'dan daha fazla taş
            offset: 15
        };
    }

    /**
     * Mesafe ölçümü oluştur (Cetvel için)
     */
    createDistanceMeasurement(
        nokta1: THREE.Vector3,
        nokta2: THREE.Vector3,
        options: {
            birim?: string;
            hassasiyet?: number;
            axisLock?: 'none' | 'horizontal' | 'vertical';
            olcumTuru?: OlcumTuru;
        } = {}
    ): OlcumSonucu {
        if (!this.manager || !this.scene) {
            return { basarili: false, birim: 'mm', tip: 'mesafe' };
        }

        const birim = options.birim || this.ayarlar.birim;
        const hassasiyet = options.hassasiyet ?? this.ayarlar.hassasiyet;

        // Referanslar oluştur
        const refA: GeometryReference = {
            type: ReferenceType.POINT,
            point: nokta1.clone()
        };

        const refB: GeometryReference = {
            type: ReferenceType.POINT,
            point: nokta2.clone()
        };

        // Axis lock belirle
        let axisLock = AxisLock.NONE;
        if (options.axisLock === 'horizontal') {
            axisLock = AxisLock.X;
        } else if (options.axisLock === 'vertical') {
            axisLock = AxisLock.Y;
        }

        // Dimension seçenekleri - DimensionSettings'den al
        const dimOptions: LinearDimensionOptions = {
            ...this.getCurrentStyleOptions(),
            precision: hassasiyet,
            unit: birim,
            axisLock
        };

        // 🎯 YÜZEYE DEĞEN UZATMA ÇİZGİSİ: Seçili yüzeylerin sınır kutusunu (bbox) ölçüye taşı →
        // uzatma çizgileri her sürüklemede yüzey kutusuna kıstırılıp yüzeye değer (küçük yüzeyde bile).
        if ((options as any).yuzeyBBox1) (dimOptions as any).yuzeyBBox1 = (options as any).yuzeyBBox1;
        if ((options as any).yuzeyBBox2) (dimOptions as any).yuzeyBBox2 = (options as any).yuzeyBBox2;

        // Dimension oluştur
        let result;
        if (axisLock !== AxisLock.NONE) {
            result = this.manager.createAxisDimension(refA, refB, axisLock, dimOptions);
        } else {
            result = this.manager.createLinearDimension(refA, refB, dimOptions);
        }
        // console.log('[DimensionIntegration] createDistanceMeasurement result:', result.success);

        if (result.success && result.dimension) {
            const mesafe = result.dimension.value;

            // 🎯 KAYDET/YÜKLE: ölçümü yeniden kurmak için serialize-edilebilir kayıt iliştir
            (result.dimension as any).userData = {
                ...((result.dimension as any).userData || {}),
                olcumKayit: { tip: options.olcumTuru || 'mesafe', n1: nokta1.toArray(), n2: nokta2.toArray(), birim, hassasiyet: options.hassasiyet ?? 2 }
            };

            return {
                basarili: true,
                mesafe,
                birim,
                tip: options.olcumTuru || 'mesafe',
                noktalar: {
                    baslangic: nokta1.clone(),
                    bitis: nokta2.clone()
                }
            };
        }

        return { basarili: false, birim, tip: 'mesafe' };
    }

    /**
     * Açı ölçümü oluştur
     */
    createAngleMeasurement(
        kenar1Baslangic: THREE.Vector3,
        kenar1Bitis: THREE.Vector3,
        kenar2Baslangic: THREE.Vector3,
        kenar2Bitis: THREE.Vector3,
        options: {
            hassasiyet?: number;
        } = {}
    ): OlcumSonucu {
        if (!this.manager || !this.scene) {
            return { basarili: false, birim: '°', tip: 'aci' };
        }

        // Kenar yönlerini hesapla
        const dir1 = new THREE.Vector3().subVectors(kenar1Bitis, kenar1Baslangic).normalize();
        const dir2 = new THREE.Vector3().subVectors(kenar2Bitis, kenar2Baslangic).normalize();

        // Referanslar oluştur
        const refA: GeometryReference = {
            type: ReferenceType.EDGE,
            point: kenar1Baslangic.clone(),
            direction: dir1
        };

        const refB: GeometryReference = {
            type: ReferenceType.EDGE,
            point: kenar2Baslangic.clone(),
            direction: dir2
        };

        // Dimension seçenekleri - DimensionSettings'den al
        const dimOptions: AngularDimensionOptions = {
            ...this.getCurrentStyleOptions(),
            precision: options.hassasiyet ?? 1,
            angleFormat: 'degrees'
        };

        // Dimension oluştur
        const result = this.manager.createAngleDimension(refA, refB, dimOptions);

        if (result.success && result.dimension) {
            return {
                basarili: true,
                aci: result.dimension.value,
                birim: '°',
                tip: 'aci'
            };
        }

        return { basarili: false, birim: '°', tip: 'aci' };
    }

    /**
     * Çap ölçümü oluştur (Kumpas için)
     * @param capUc1 - Opsiyonel: Çapın bir ucu (FikoOlcumCizgisi uyumluluğu için)
     * @param capUc2 - Opsiyonel: Çapın diğer ucu
     */
    createDiameterMeasurement(
        merkez: THREE.Vector3,
        yaricap: number,
        normal?: THREE.Vector3,
        options: {
            birim?: string;
            hassasiyet?: number;
            capUc1?: THREE.Vector3;  // 🎯 FikoOlcumCizgisi uyumluluğu: Çap uçları
            capUc2?: THREE.Vector3;
        } = {}
    ): OlcumSonucu {
        if (!this.manager || !this.scene) {
            return { basarili: false, birim: 'mm', tip: 'cap' };
        }

        const birim = options.birim || this.ayarlar.birim;

        // Referans oluştur - 🎯 Çap uçlarını da ekle
        const ref: GeometryReference = {
            type: ReferenceType.CIRCLE,
            center: merkez.clone(),
            radius: yaricap,
            axis: normal?.clone() || new THREE.Vector3(0, 0, 1),
            // 🎯 FikoOlcumCizgisi uyumluluğu: Çap uçları (point = capUc1, direction'dan capUc2 hesaplanabilir)
            point: options.capUc1?.clone(),
            direction: options.capUc2 ? new THREE.Vector3().subVectors(options.capUc2, merkez).normalize() : undefined
        };

        // Dimension seçenekleri - DimensionSettings'den al
        const dimOptions: DiameterDimensionOptions = {
            ...this.getCurrentStyleOptions(),
            precision: options.hassasiyet ?? this.ayarlar.hassasiyet,
            unit: birim,
            prefix: '⌀',
            showCenterMark: true
        };

        // Dimension oluştur
        const result = this.manager.createDiameterDimension(ref, dimOptions);

        if (result.success && result.dimension) {
            // 🎯 KAYDET/YÜKLE: çap ölçümünü yeniden kurmak için kayıt iliştir
            (result.dimension as any).userData = {
                ...((result.dimension as any).userData || {}),
                olcumKayit: { tip: 'cap', merkez: merkez.toArray(), yaricap, n1: options.capUc1?.toArray(), n2: options.capUc2?.toArray(), normal: normal?.toArray(), birim, hassasiyet: options.hassasiyet ?? 2 }
            };
            return {
                basarili: true,
                cap: result.dimension.value,
                yaricap,
                birim,
                tip: 'cap',
                merkezBilgisi: {
                    merkez: merkez.clone(),
                    yaricap,
                    tip: 'circle'
                }
            };
        }

        return { basarili: false, birim, tip: 'cap' };
    }

    /**
     * Yarıçap ölçümü oluştur (Kumpas için)
     * @param surfacePoint - Eğri yüzeylerde okun konumlandırılacağı gerçek yüzey noktası
     */
    createRadiusMeasurement(
        merkez: THREE.Vector3,
        yaricap: number,
        normal?: THREE.Vector3,
        options: {
            birim?: string;
            hassasiyet?: number;
            surfacePoint?: THREE.Vector3;  // Eğri yüzeyler için gerçek yüzey noktası
        } = {}
    ): OlcumSonucu {
        if (!this.manager || !this.scene) {
            return { basarili: false, birim: 'mm', tip: 'yaricap' };
        }

        const birim = options.birim || this.ayarlar.birim;

        // Referans oluştur
        const ref: GeometryReference = {
            type: ReferenceType.CIRCLE,
            center: merkez.clone(),
            radius: yaricap,
            axis: normal?.clone() || new THREE.Vector3(0, 0, 1),
            surfacePoint: options.surfacePoint?.clone()  // Eğri yüzey noktası
        };

        // Dimension seçenekleri - DimensionSettings'den al
        const dimOptions: DiameterDimensionOptions = {
            ...this.getCurrentStyleOptions(),
            precision: options.hassasiyet ?? this.ayarlar.hassasiyet,
            unit: birim,
            prefix: 'R',
            showCenterMark: true
        };

        // Dimension oluştur
        const result = this.manager.createRadiusDimension(ref, dimOptions);

        if (result.success && result.dimension) {
            return {
                basarili: true,
                yaricap: result.dimension.value,
                birim,
                tip: 'yaricap',
                merkezBilgisi: {
                    merkez: merkez.clone(),
                    yaricap,
                    tip: 'circle'
                }
            };
        }

        return { basarili: false, birim, tip: 'yaricap' };
    }

    /**
     * 🎯 v2.1.0: Koordinat ölçümü oluştur (Kumpas için)
     * Nokta seçildiğinde X, Y, Z koordinatlarını 3D sahneye yazar
     */
    createCoordinateMeasurement(
        nokta: THREE.Vector3,
        options: {
            birim?: string;
            hassasiyet?: number;
        } = {}
    ): OlcumSonucu {
        if (!this.manager || !this.scene) {
            return { basarili: false, birim: 'mm', tip: 'koordinat' as OlcumTuru };
        }

        const birim = options.birim || this.ayarlar.birim;
        const hassasiyet = options.hassasiyet ?? this.ayarlar.hassasiyet;

        // Referans oluştur
        const ref: GeometryReference = {
            type: ReferenceType.POINT,
            point: nokta.clone()
        };

        // Dimension seçenekleri
        const dimOptions = {
            ...this.getCurrentStyleOptions(),
            precision: hassasiyet,
            unit: birim,
            showLeaderLine: true,
            leaderLength: 15,
            leaderAngle: 45
        };

        // Dimension oluştur
        const result = this.manager.createCoordinateDimension(ref, dimOptions);

        if (result.success && result.dimension) {
            return {
                basarili: true,
                birim,
                tip: 'koordinat' as OlcumTuru,
                noktalar: {
                    baslangic: nokta.clone(),
                    bitis: nokta.clone()
                }
            };
        }

        return { basarili: false, birim, tip: 'koordinat' as OlcumTuru };
    }

    /**
     * Mevcut FikoOlcumCizgisi'nden dimension oluştur
     * (Geriye uyumluluk için)
     */
    createFromFikoOlcumCizgisi(
        olcum: FikoOlcumCizgisi,
        options: {
            birim?: string;
        } = {}
    ): OlcumSonucu {
        if (!this.manager || !this.scene) {
            return { basarili: false, birim: 'mm', tip: 'mesafe' };
        }

        const birim = options.birim || this.ayarlar.birim;

        // FikoOlcumCizgisi'nden bilgileri al
        const olcumTuru = olcum.olcumTuru;

        switch (olcumTuru) {
            case 'cap':
                // Çap bilgilerini al (eğer varsa)
                // FikoOlcumCizgisi'nde merkez ve yarıçap bilgisi gerekli
                break;

            case 'yaricap':
                // Yarıçap bilgilerini al
                break;

            case 'aci':
                // Açı bilgilerini al
                break;

            case 'mesafe':
            case 'uzunluk':
            default:
                // Mesafe/uzunluk için nokta bilgilerini al
                // FikoOlcumCizgisi internal state'inden
                break;
        }

        return { basarili: false, birim, tip: olcumTuru };
    }

    /**
     * Tüm dimension'ları temizle
     */
    clearAll(): void {
        this.manager?.clearAll();
    }

    /**
     * Dimension'ı sil
     */
    removeDimension(id: string): boolean {
        return this.manager?.removeDimension(id) || false;
    }

    /**
     * Tüm dimension'ları güncelle
     */
    updateAll(): void {
        this.manager?.updateAll();
    }

    /**
     * Ayarları güncelle
     */
    updateSettings(ayarlar: Partial<typeof this.ayarlar>): void {
        this.ayarlar = { ...this.ayarlar, ...ayarlar };
    }

    /**
     * Manager'a erişim
     */
    getManager(): DimensionManager | null {
        return this.manager;
    }

    /**
     * Servisi temizle
     */
    dispose(): void {
        // 🎯 v2.0.0: Sürükleme desteğini kapat
        if (this.manager) {
            this.manager.disableDragSupport();
        }

        // Interaction manager temizle
        if (this.interactionManager) {
            this.interactionManager.dispose();
            this.interactionManager = null;
        }

        this.manager?.dispose();
        this.manager = null;
        this.scene = null;
        this.fikoOlcumlerGrubu = null;
        this.canvas = null;
    }

    /**
     * 🎯 v2.0.0: Sürükleme aktif mi kontrol et
     */
    isDragEnabled(): boolean {
        return this.ayarlar.surukleAktif;
    }
}

// Singleton instance
export const dimensionIntegrationService = new DimensionIntegrationService();
